import React, { useState, useCallback } from 'react';
import { errorToast, successToast } from '../../../utils/helper';
import SingleExerciseForm from './SingleExerciseForm';
import ExerciseSelectorPanel from './ExerciseSelectorPanel';
import { useWorkoutCreation } from '../../../context/WorkoutCreationContext';
import { useHealthLoading } from '../../../context/HealthLoadingContext';
import WorkoutDraggableList from './WorkoutDraggableList';
import WorkoutSuccessModal from '../../HealthComponent/WorkoutSuccessModal';

const ExercisesTab = ({ error, isView, onPreviewAndPublish }) => {
  // Use workout creation context
  const { workoutData, updateWorkoutData, submitWorkout } = useWorkoutCreation();
  const { withLoading } = useHealthLoading();

  // Initialize with empty exercise for form
  const [currentExercise, setCurrentExercise] = useState({
    id: Date.now(),
    name: '',
    equipment_id: '',
    muscle_groups: [],
    exercise_duration: '',
    video_url: '',
    image_url: '',
    video: null,
    videoPreview: '',
    videoDurationMin: '00',
    videoDurationSec: '00',
    image: null,
    imagePreview: ''
  });

  const [exerciseErrors, setExerciseErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const [mode, setMode] = useState('select');

  // Update current exercise when user makes changes
  const handleExerciseChange = useCallback((updatedExercise) => {
    setCurrentExercise(updatedExercise);
    setExerciseErrors({});
  }, []);

  // Handle cancel edit mode
  const handleCancelEdit = useCallback(() => {
    // Reset to empty form
    const resetExercise = {
      id: Date.now(),
      name: '',
      equipment_id: '',
      muscle_groups: [],
      exercise_duration: '',
      video_url: '',
      image_url: '',
      video: null,
      videoPreview: '',
      videoDurationMin: '00',
      videoDurationSec: '00',
      image: null,
      imagePreview: ''
    };
    setCurrentExercise(resetExercise);
    setIsEditing(false);
    setEditingExerciseId(null);
    setExerciseErrors({});
  }, []);

  const handleAddExistingExercise = useCallback(
    (selectedExercise) => {
      const existingExercises = workoutData.exercises || [];
      const exerciseExists = existingExercises.some((exercise) => {
        const existingId = exercise.exercise_id?._id || exercise.exercise_id || exercise._id;
        return existingId === selectedExercise.exercise_id;
      });

      if (exerciseExists) {
        errorToast('This exercise has already been added to the workout');
        return;
      }

      const updatedExercises = [
        ...existingExercises,
        {
          ...selectedExercise,
          sequence: existingExercises.length + 1,
          metadata: selectedExercise.metadata || { sets: [] }
        }
      ];

      updateWorkoutData({ exercises: updatedExercises });
      successToast('Exercise added to workout successfully!');
    },
    [updateWorkoutData, workoutData.exercises]
  );

  // Handle save exercise - receives the saved exercise data from SingleExerciseForm
  const handleSaveExercise = useCallback(
    async (savedExerciseData) => {
      try {
        // Check if we have the saved exercise ID
        if (!savedExerciseData?._id) {
          errorToast('Exercise must be saved before adding to workout');
          return;
        }

        setIsSaving(true);

        // Get existing exercises from context
        const existingExercises = workoutData.exercises || [];

        // Check if we're editing an existing exercise
        if (isEditing && editingExerciseId) {
          // Update existing exercise in the list
          const updatedExercises = existingExercises.map((exercise) => {
            const exerciseId = exercise.exercise_id?._id || exercise.exercise_id || exercise._id;
            if (exerciseId === editingExerciseId) {
              return {
                ...exercise,
                exercise_id: savedExerciseData._id,
                _id: savedExerciseData._id,
                name: savedExerciseData.name,
                muscle_groups: savedExerciseData.muscle_groups || [],
                equipment_id:
                  savedExerciseData.equipment_id?._id || savedExerciseData.equipment_id || '',
                exercise_duration: savedExerciseData.exercise_duration,
                video_url: savedExerciseData.video_url || '',
                image_url: savedExerciseData.image_url || ''
              };
            }
            return exercise;
          });

          // Update context
          updateWorkoutData({
            exercises: updatedExercises
          });

          successToast('Exercise updated successfully!');

          // Reset editing state
          setIsEditing(false);
          setEditingExerciseId(null);
        } else {
          // Adding new exercise - check if exercise already exists
          const exerciseExists = existingExercises.some((e) => {
            const existingId = e.exercise_id?._id || e.exercise_id || e._id;
            return existingId === savedExerciseData._id;
          });

          if (exerciseExists) {
            errorToast('This exercise has already been added to the workout');
            setIsSaving(false);
            return;
          }

          // Calculate next sequence
          const nextSequence = existingExercises.length + 1;

          // Add new exercise to existing list - flatten the structure like in NewWorkout.jsx
          const updatedExercises = [
            ...existingExercises,
            {
              exercise_id: savedExerciseData._id,
              _id: savedExerciseData._id,
              name: savedExerciseData.name,
              muscle_groups: savedExerciseData.muscle_groups || [],
              equipment_id:
                savedExerciseData.equipment_id?._id || savedExerciseData.equipment_id || '',
              exercise_duration: savedExerciseData.exercise_duration,
              video_url: savedExerciseData.video_url || '',
              image_url: savedExerciseData.image_url || '',
              sequence: nextSequence,
              metadata: { sets: [] }
            }
          ];

          // Update context
          updateWorkoutData({
            exercises: updatedExercises
          });

          successToast('Exercise added to workout successfully!');
        }

        // Reset the form for next exercise
        const resetExercise = {
          id: Date.now(),
          name: '',
          equipment_id: '',
          muscle_groups: [],
          exercise_duration: '',
          video_url: '',
          image_url: '',
          video: null,
          videoPreview: '',
          videoDurationMin: '00',
          videoDurationSec: '00',
          image: null,
          imagePreview: ''
        };
        setCurrentExercise(resetExercise);
        setExerciseErrors({});
      } catch (error) {
        console.error('Error saving exercise to workout:', error);
        errorToast(error.message || 'Failed to save exercise to workout');
      } finally {
        setIsSaving(false);
      }
    },
    [workoutData, updateWorkoutData, isEditing, editingExerciseId]
  );

  // Handle reorder exercises
  const handleReorderExercises = useCallback(
    (reorderedData) => {
      const existingExercises = workoutData.exercises || [];

      // Update with new sequences
      const reorderedExercises = existingExercises.map((exercise) => {
        const exerciseId = exercise.exercise_id?._id || exercise.exercise_id || exercise._id;
        const reorderedItem = reorderedData.find(
          (item) => (item.exercise_id?._id || item.exercise_id) === exerciseId
        );
        return {
          ...exercise,
          sequence: reorderedItem?.sequence || exercise.sequence
        };
      });

      // Sort by sequence
      reorderedExercises.sort((a, b) => a.sequence - b.sequence);

      updateWorkoutData({
        exercises: reorderedExercises
      });

      successToast('Exercise order updated!');
    },
    [workoutData.exercises, updateWorkoutData]
  );

  // Handle edit exercise - populate form with existing data
  const handleEditExercise = useCallback((exercise) => {
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    console.log('Editing exercise:', exercise);
    // Populate form with existing exercise data
    const exerciseToEdit = {
      id: exercise.exercise_id,
      name: exercise.name || '',
      equipment_id: exercise.equipment_id?._id || exercise.equipment_id || '',
      muscle_groups: exercise.muscle_groups || [],
      exercise_duration: exercise.exercise_duration || '',
      video_url: exercise.video_url || '',
      image_url: exercise.image_url || '',
      video: null,
      videoPreview: exercise.video_url || '',
      videoDurationMin: '00',
      videoDurationSec: '00',
      image: null,
      imagePreview: exercise.image_url || ''
    };

    setCurrentExercise(exerciseToEdit);
    setMode('create');
    setIsEditing(true);
    setEditingExerciseId(exercise.exercise_id);
    setExerciseErrors({});
  }, []);

  // Handle delete exercise
  const handleDeleteExercise = useCallback(
    async (exerciseId) => {
      const existingExercises = workoutData.exercises || [];

      const updatedExercises = existingExercises
        .filter((e) => {
          const eId = e.exercise_id?._id || e.exercise_id || e._id;
          return eId !== exerciseId;
        })
        .map((exercise, index) => ({
          ...exercise,
          sequence: index + 1
        }));

      updateWorkoutData({
        exercises: updatedExercises
      });
      successToast('Exercise removed successfully!');
    },
    [workoutData.exercises, updateWorkoutData]
  );

  // Publish handler
  const handlePublish = useCallback(async () => {
    await withLoading(async () => {
      try {
        const result = await submitWorkout();
        if (result?.success) {
          setShowSuccessModal(true);
        } else {
          throw new Error(result?.error || 'Failed to publish workout');
        }
      } catch (err) {
        console.error('Error publishing workout:', err);
        errorToast(err.message || 'Failed to publish workout');
      }
    });
  }, [submitWorkout, withLoading]);

  return (
    <div className="space-y-6">
      {/* {!isView && (
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setMode('select')}
            className={mode === 'select' ? 'btn-primary' : 'btn-secondary'}
          >
            Select Existing Exercise
          </button>
          <button
            type="button"
            onClick={() => setMode('create')}
            className={mode === 'create' ? 'btn-primary' : 'btn-secondary'}
          >
            Add New Exercise
          </button>
        </div>
      )} */}
      {workoutData.exercises && workoutData.exercises.length > 0 && (
        <WorkoutDraggableList
          items={workoutData.exercises}
          setItems={(items) => updateWorkoutData({ exercises: items })}
          droppableId="exercises-list"
          heading={`Added Exercises (${workoutData.exercises.length})`}
          onSubmit={handleReorderExercises}
          onDelete={handleDeleteExercise}
          onEdit={handleEditExercise}
          type="exercise"
          hideSubmitButton={true}
        />
      )}
      {mode === 'select' && !isEditing ? (
        <ExerciseSelectorPanel
          type="exercise"
          selectedItems={workoutData.exercises || []}
          onAddItem={handleAddExistingExercise}
          isView={isView}
        />
      ) : (
        <SingleExerciseForm
          exercise={currentExercise}
          onExerciseChange={handleExerciseChange}
          onSave={handleSaveExercise}
          onCancel={handleCancelEdit}
          isView={isView}
          showSaveButton={true}
          exerciseErrors={exerciseErrors}
          type="exercise"
          isSaving={isSaving}
          isEditing={isEditing}
          exerciseId={editingExerciseId}
        />
      )}

      {/* Draggable List of Saved Exercises */}

      {error?.exercises && (
        <span className="text-red-500 text-[12px] mt-2 block">{error.exercises}</span>
      )}

      {/* Action Buttons: Publish */}
      {!isView && (
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={onPreviewAndPublish ?? handlePublish}
            className="btn-primary"
          >
            Preview and Publish
          </button>
        </div>
      )}

      {/* Success Modal */}
      <WorkoutSuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
    </div>
  );
};

export default ExercisesTab;
